import React, { useEffect, useRef, useState } from "react";
import { Check, Sparkle } from "lucide-react";
import QuoteModal from "./QuoteModal";
import "./PricingSection.css";

const PricingSection = () => {
  const pricingRef = useRef(null);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  useEffect(() => {
    const element = pricingRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 },
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const scrollToContact = (serviceName) => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });

      const serviceDropdown = document.getElementById("service");
      if (serviceDropdown && serviceName) {
        serviceDropdown.value = serviceName;
        const event = new Event("change", { bubbles: true });
        serviceDropdown.dispatchEvent(event);
      }
    }
  };

  return (
    <section id="pricing" className="pricing-section" ref={pricingRef} aria-label="Packages and pricing">
      <div className="pricing-container">
        {/* Social Media Campaigns */}
        <div className="pricing-group" aria-labelledby="pricing-social-title">
          <div className="pricing-header">
            <span className="pricing-label">SOCIAL MEDIA CAMPAIGNS</span>
            <h2 className="pricing-title" id="pricing-social-title">Dominate Your Social Presence</h2>
            <p className="pricing-subtitle">
              Consistent, high-impact content designed to amplify your brand
              across all social platforms.
            </p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="card-header">
                <h3 className="plan-name">Basic Plan</h3>
                <div className="plan-price">
                  <span className="price-amount">60k</span>
                  <span className="price-period">/ Per Month</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Multiple Social media accounts management</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>15 static posters</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>5 reels</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("Social Media Services")}
              >
                Select Plan
              </button>
            </div>

            <div className="pricing-card popular">
              <div className="popular-badge">
                <Sparkle size={14} />
                <span>MOST POPULAR</span>
              </div>
              <div className="card-header">
                <h3 className="plan-name">Pro Plan</h3>
                <div className="plan-price">
                  <span className="price-amount">70k</span>
                  <span className="price-period">/ Per Month</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Multiple Social media accounts management</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>20 static posters</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>7 reels</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("Social Media Services")}
              >
                Select Plan
              </button>
            </div>

            <div className="pricing-card">
              <div className="card-header">
                <h3 className="plan-name">Ultimate Plan</h3>
                <div className="plan-price">
                  <span className="price-amount">90k</span>
                  <span className="price-period">/ Per Month</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Branding</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Multiple Social media accounts management</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>30 static posters</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>10 reels</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("Social Media Services")}
              >
                Select Plan
              </button>
            </div>
          </div>
        </div>

        {/* Video Production */}
        <div className="pricing-group" aria-labelledby="pricing-video-title">
          <div className="pricing-header">
            <span className="pricing-label">YOUR INVESTMENTS</span>
            <h2 className="pricing-title" id="pricing-video-title">Cinematic Video Production</h2>
            <p className="pricing-subtitle">
              World-class video content crafted to tell your story and drive
              conversions.
            </p>
          </div>
          <div className="pricing-grid video-grid">
            <div className="pricing-card video-card">
              <div className="card-header">
                <h3 className="plan-name">Talking Head Ad</h3>
                <div className="plan-price">
                  <span className="price-amount">19k</span>
                  <span className="price-period">/ video</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Script</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Professional Model</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Shooting</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Editing</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("Single Ad")}
              >
                Get Started
              </button>
            </div>

            <div className="pricing-card video-card">
              <div className="card-header">
                <h3 className="plan-name">Product Ad</h3>
                <div className="plan-price">
                  <span className="price-amount">29k</span>
                  <span className="price-period">/ video</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Unique Concept</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Premium ad design</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Creative direction</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Editing</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("Single Ad")}
              >
                Get Started
              </button>
            </div>

            <div className="pricing-card video-card">
              <div className="card-header">
                <h3 className="plan-name">Story Based Ad</h3>
                <div className="plan-price">
                  <span className="price-amount">30k</span>
                  <span className="price-period">/ video</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Story based script</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Film style direction</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Production</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Post production</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("DVC, TVC or SF")}
              >
                Get Started
              </button>
            </div>

            <div className="pricing-card video-card">
              <div className="card-header">
                <h3 className="plan-name">DVC, TVC & SF</h3>
                <div className="plan-price">
                  <span className="price-amount">Custom</span>
                </div>
              </div>
              <ul className="features-list">
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Pre Production</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Production</span>
                </li>
                <li className="feature-item">
                  <Check size={18} className="check-icon" />
                  <span>Post Production</span>
                </li>
              </ul>
              <button
                className="btn-select"
                onClick={() => scrollToContact("DVC, TVC or SF")}
              >
                Get Quote
              </button>
            </div>
          </div>
        </div>

        {/* FIXED: This button now opens the QuoteModal instead of just scrolling */}
        <div className="pricing-cta-banner">
          <h3 className="cta-banner-title">Need a Custom Quote?</h3>
          <p className="cta-banner-text">
            Get a detailed quote tailored to your specific project needs
          </p>
          <button
            className="btn-get-quote"
            onClick={() => setIsQuoteModalOpen(true)}
          >
            Get a Quote
          </button>
        </div>
      </div>

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
      />
    </section>
  );
};

export default PricingSection;
